import { useFormContext, Controller } from 'react-hook-form';
import { Autocomplete, type AutocompleteProps, TextField } from '@mui/material';

// ----------------------------------------------------------------------

interface Props<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  name: string;
  label?: string;
  helperText?: React.ReactNode;
  InputLabelProps?: object;
  required?: boolean;
}

export function RHFAutocomplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>({
  name,
  label,
  helperText,
  required,
  InputLabelProps,
  ...other
}: Omit<Props<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'>) {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          onChange={(event, newValue, reason) => {
            setValue(name, newValue, { shouldValidate: true, shouldDirty: true });
            if (other.onChange) {
              other.onChange(event, newValue, reason);
            }
          }}
          onInputChange={(event, newInputValue, reason) => {
            setValue(name, newInputValue, { shouldValidate: true, shouldDirty: true });
            if (other.onInputChange) {
              other.onInputChange(event, newInputValue, reason);
            }
          }}
          renderInput={(params) => (
            <TextField
              label={label}
              error={!!error}
              helperText={error ? error?.message : helperText}
              {...params}
              required={required}
              InputLabelProps={InputLabelProps}
            />
          )}
          {...other}
        />
      )}
    />
  );
}
